import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { showToast } from '../components/base/Toast';

export function useLikes(userId: string | undefined) {
  const [likedPostIds, setLikedPostIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // 로그인한 사용자의 좋아요 목록 로드
  const loadUserLikes = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await supabase
        .from('likes')
        .select('post_id')
        .eq('user_id', userId)
        .not('post_id', 'is', null);

      if (data) {
        setLikedPostIds(new Set(data.map(l => l.post_id).filter(Boolean)));
      }
    } catch (error) {
      console.error('Error loading likes:', error);
    }
  }, [userId]);

  useEffect(() => {
    loadUserLikes();
  }, [loadUserLikes]);

  const isLiked = useCallback(
    (postId: string) => likedPostIds.has(postId),
    [likedPostIds]
  );

  const toggleLike = useCallback<
    (postId: string) => Promise<{ liked: boolean; delta: number } | null>
  >(async (postId) => {
    if (!userId) return null;
    if (loading) return null;

    setLoading(true);
    const wasLiked = likedPostIds.has(postId);

    // 낙관적 업데이트
    setLikedPostIds((prev) => {
      const next = new Set(prev);
      if (wasLiked) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });

    try {
      if (wasLiked) {
        // 좋아요 취소
        await supabase
          .from('likes')
          .delete()
          .eq('user_id', userId)
          .eq('post_id', postId);
        await supabase.rpc('decrement_likes', { post_id: postId });
        return { liked: false, delta: -1 };
      } else {
        // 좋아요
        await supabase
          .from('likes')
          .insert({ user_id: userId, post_id: postId });
        await supabase.rpc('increment_likes', { post_id: postId });

        // 게시글 작성자에게 알림 생성
        try {
          const { data: post } = await supabase
            .from('posts')
            .select('user_id, title')
            .eq('id', postId)
            .maybeSingle();

          if (post && post.user_id !== userId) {
            const { data: liker } = await supabase
              .from('profiles')
              .select('display_name')
              .eq('id', userId)
              .maybeSingle();

            const likerName = liker?.display_name || '누군가';
            
            await supabase.from('notifications').insert({
              user_id: post.user_id,
              type: 'like',
              message: `${likerName}님이 회원님의 게시글을 좋아합니다`,
              post_id: postId,
              is_read: false,
            });
          }
        } catch (notifError) {
          console.error('알림 생성 실패:', notifError);
        }

        return { liked: true, delta: 1 };
      }
    } catch (error) {
      // 실패 시 롤백
      setLikedPostIds((prev) => {
        const next = new Set(prev);
        if (wasLiked) {
          next.add(postId);
        } else {
          next.delete(postId);
        }
        return next;
      });
      console.error('Error toggling like:', error);
      showToast('좋아요 처리 중 오류가 발생했습니다', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, likedPostIds, loading]);

  return { isLiked, toggleLike, loadUserLikes };
}
